import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { format, Locale, startOfMonth } from "date-fns";
import { es, enUS, fr } from "date-fns/locale";

import { JournalHeader } from "../components/journal/JournalHeader";
import { JournalTabs } from "../components/journal/JournalTabs";
import { JournalContent } from "../components/journal/JournalContent";
import { JournalAnalysisSection } from "../components/journal/JournalAnalysisSection";
import JournalDateSelector from "../components/journal/JournalDateSelector";

import { useJournal } from "../hooks/useJournal";
import { useJournalEntryState } from "../hooks/useJournalEntryState";

import { JournalEntryType, JournalEntryData, EntryMetrics } from "../types/journal";

type LocaleKey = 'es' | 'en' | 'fr';
const locales: Record<LocaleKey, Locale> = { es, en: enUS, fr };

const JournalPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<JournalEntryType>("morning");
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const {
    entries,
    isLoading,
    error,
    analysis,
    fetchAnalysis,
    fetchEntries,
    createEntry,
    updateEntry,
  } = useJournal();

  const { existingEntry, isToday } = useJournalEntryState(entries, selectedDate, activeTab);

  useEffect(() => {
    const date = format(selectedDate, "yyyy-MM-dd");
    const hasMorningEntry = entries.some(
      (entry) => format(entry.date, "yyyy-MM-dd") === date && entry.type === "morning"
    );
    const hasEveningEntry = entries.some(
      (entry) => format(entry.date, "yyyy-MM-dd") === date && entry.type === "evening"
    );

    if (hasMorningEntry && hasEveningEntry) {
      fetchAnalysis(selectedDate);
    }
  }, [entries, selectedDate, fetchAnalysis]);

  useEffect(() => {
    const currentLocale = locales[i18n.language as LocaleKey] || locales.en;
    const monthStart = startOfMonth(selectedDate);
    fetchEntries(monthStart);
  }, [selectedDate, fetchEntries, i18n.language]);

  const handleSaveEntry = async (data: { content: string; metrics: EntryMetrics }): Promise<void> => {
    try {
      const entryData: JournalEntryData = {
        date: selectedDate,
        type: activeTab,
        content: data.content,
        metrics: data.metrics,
      };

      if (existingEntry) {
        await updateEntry(entryData);
        toast(t("journal.updateSuccess"));
      } else {
        await createEntry(entryData);
        toast(t("journal.saveSuccess"));
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving entry:", error);
      toast.error(t("journal.saveError"));
    }
  };

  const handleDateSelect = (date: Date): void => {
    setSelectedDate(date);
    setIsEditing(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
      >
        {/* Header Section */}
        <JournalHeader
          isToday={isToday}
          existingEntry={existingEntry}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />

        {/* Main Content Section */}
        <div className="p-4 sm:p-6 space-y-6">
          {error && (
            <div className="p-3 sm:p-4 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg text-sm">
              {error}
            </div>
          )}

          <JournalDateSelector
            entries={entries}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
          />

          {/* Tabs Section */}
          <JournalTabs activeTab={activeTab} setActiveTab={setActiveTab} />

          {/* Content Section */}
          <div className="relative min-h-[300px]">
            <JournalContent
              isLoading={isLoading}
              isToday={isToday}
              isEditing={isEditing}
              activeTab={activeTab}
              selectedDate={selectedDate}
              existingEntry={existingEntry}
              handleSaveEntry={handleSaveEntry}
              onEdit={() => setIsEditing(true)}
            />
          </div>

          {/* Analysis Section */}
          {!isEditing && existingEntry && analysis && (
            <JournalAnalysisSection analysis={analysis} />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default JournalPage;